/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable @next/next/no-img-element */
import { Children, useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import slugify from '@sindresorhus/slugify';
import Highlight, { defaultProps } from 'prism-react-renderer';
import theme from 'prism-react-renderer/themes/nightOwl';
import cc from 'classnames';
import { slugifyHeading } from '@/utils/blog';

function CodeBlock(props: any) {
  const { children: codeChildren } = props;
  const { children, className } = codeChildren.props;

  const language = className && className.replace(/lang(uage)?-/, ``);

  if (codeChildren) {
    return (
      <Highlight
        {...defaultProps}
        code={children}
        language={language}
        theme={theme}
      >
        {({
          className: codeClassName,
          tokens,
          getLineProps,
          getTokenProps,
        }) => (
          <pre className={cc([`relative`, codeClassName])}>
            {tokens.map((line, i) => {
              // Checks if the last line is empty. We have a problem with a \n being added to the end of the code block
              if (i === tokens.length - 1 && line[0].empty) return null;

              return (
                <div key={i} {...getLineProps({ line, key: i })}>
                  {line.map((token, key) => (
                    <span key={key} {...getTokenProps({ token, key })} />
                  ))}
                </div>
              );
            })}
          </pre>
        )}
      </Highlight>
    );
  }

  return <code {...props} />;
}

function toSlug(string: string) {
  return slugify(string, { lowercase: true });
}

/**
 * as = 'blog' | 'docs'
 */
export function Heading({ children, level, as = `docs` }: any) {
  const Component = `h${level}` as keyof JSX.IntrinsicElements;
  /**
   * There are three types of children that we can receive here.
   *
   * 1. If we have a normal heading, we'll receive a string.
   * Ex: ## Api Playground
   *
   * 2. If we have a heading with backticks in the begging and in the end, we'll receive an object
   * Ex: ## `Api Playground`
   *
   * 3. If we have backticks inside words of the heading, we'll receive an array of strings and objects.
   * Ex: ## `Api` Playground
   * Object example:
   [
    {
      ...
      props: {
        parentName: 'h2',
        originalType: 'inlineCode',
        mdxType: 'inlineCode',
        children: 'Public'
      },
    },
    ' API Permissions'
  ]
   */

  let title = children?.props?.children || children;

  if (typeof title === `object`) {
    const parsedTitle = title.map(
      (child: any) => child?.props?.children || child,
    );

    title = parsedTitle.join(``);
  }

  const slug = as === `blog` ? slugifyHeading(title) : toSlug(title);

  return (
    <Component
      id={slug}
      className="group relative whitespace-pre-wrap"
      style={{
        scrollMarginTop: `100px`,
      }}
    >
      <span>{children}</span>
      <a
        id="heading-anchor"
        href={`#${slug}`}
        aria-label="Anchor"
        className="ml-[10px] text-[length:inherit] !no-underline  text-gray-500 opacity-0 group-hover:opacity-100 transition-opacity duration-100 after:content-['#'] after:text-gray-500"
      >
        <span className="sr-only">Anchor</span>
      </a>
    </Component>
  );
}

function Icon({ type = `default` }) {
  if (!type) return null;

  switch (type) {
    case `warning`:
      return (
        <span className="flex-no-shrink pt-6">
          <svg
            className="fill-current w-6 h-6"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
          >
            <path fill="none" d="M0 0h24v24H0z" />
            <path d="M12 22C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10 10-4.477 10-10 10zm0-2a8 8 0 1 0 0-16 8 8 0 0 0 0 16zM11 7h2v2h-2V7zm0 4h2v6h-2v-6z" />
          </svg>
        </span>
      );
    case `danger`:
      return (
        <span className="flex-no-shrink pt-6">
          <svg
            className="fill-current w-6 h-6"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
          >
            <path fill="none" d="M0 0h24v24H0z" />
            <path d="M12 22C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10 10-4.477 10-10 10zm0-2a8 8 0 1 0 0-16 8 8 0 0 0 0 16zm-1-5h2v2h-2v-2zm0-8h2v6h-2V7z" />
          </svg>
        </span>
      );
    default:
      return null;
  }
}

const alertVariants: { [key: string]: string } = {
  default: `border-primary-400 bg-primary-50 text-primary-400`,
  warning: `border-orange-500 bg-orange-100 text-orange-500`,
  danger: `border-red-500 bg-red-100 text-red-500`,
};

function Alert({ type = `default`, children }: any) {
  return (
    <div>
      <div
        className={cc(
          `border-l-4 mb-8 px-4 py-px flex space-x-3`,
          alertVariants[type],
        )}
      >
        <Icon type={type} />
        {children}
      </div>
    </div>
  );
}

function TabsItem() {
  return null;
}

function Tabs({ children }: any) {
  const items = useRef(Children.toArray(children));
  const [activeItem, setActiveItem] = useState();

  useEffect(() => {
    if (items) {
      const [firstItem] = items.current;
      /* @ts-ignore */
      setActiveItem(firstItem);
    }
  }, []);

  if (!items) return null;

  return (
    <div className="bg-big-stone rounded">
      <div className="overflow-hidden px-3 pt-3">
        {items.current.map((item, index) => {
          const isActive = item === activeItem;

          return (
            <button
              key={index}
              type="button"
              /* @ts-ignore */
              onClick={() => setActiveItem(item)}
              className={cc([
                `appearance-none focus:outline-none text-sm py-2 px-3 font-medium duration-100 ease-in-out transition-opacity rounded uppercase tracking-wider`,
                {
                  'text-white bg-light-bunting': isActive,
                  'text-light-haiti hover:text-white': !isActive,
                },
              ])}
            >
              {/* @ts-ignore */}
              {item.props.label}
            </button>
          );
        })}
      </div>
      {items.current.map((item, index) => {
        const isActive = item === activeItem;

        return (
          <div
            key={index}
            className={cc([
              /* @ts-ignore */
              item.props.className,
              `overflow-auto`,
              {
                hidden: !isActive,
              },
            ])}
          >
            {/* @ts-ignore */}
            {item.props.children}
          </div>
        );
      })}
    </div>
  );
}

function ImageBlock({ alt, ...props }: any) {
  return (
    <figure className="relative flex flex-col justify-center items-center border border-primary-150 p-4 my-2 bg-white rounded">
      <img {...props} alt={alt} loading="lazy" />
      <figcaption className="text-sm text-gray-500">{alt}</figcaption>
    </figure>
  );
}

function Code(props: any) {
  return <code {...props} />;
}

export const components = {
  pre: CodeBlock,
  code: Code,
  h1: (props: any) => <Heading level="1" {...props} />,
  h2: (props: any) => <Heading level="2" {...props} />,
  h3: (props: any) => <Heading level="3" {...props} />,
  h4: (props: any) => <Heading level="4" {...props} />,
  Alert,
  Tabs,
  Link: (props: any) => <Link {...props} prefetch={false} />,
  TabsItem,
  img: ImageBlock,
};
